import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { PageHead, Panel, StatCard } from "@/project/components/dashboard-shell";
import { domainName } from "@/project/data/network";
import { useApplications, useReviewApplication } from "@/project/lib/queries";
import { downloadCsv } from "@/project/lib/receipt";
import { AIFraudDetector } from "@/project/components/ai-fraud-detector";

export const Route = createFileRoute("/admin/applications")({ component: AdminApplications });

function AdminApplications() {
  const { data: applications = [], isLoading, error } = useApplications();
  const review = useReviewApplication();
  const [note, setNote] = useState<Record<string, string>>({});

  const count = (s: string) => applications.filter((a) => a.status === s).length;

  async function act(id: string, status: "approved" | "rejected") {
    try {
      await review.mutateAsync({ id, status, note: note[id] });
      toast.success(status === "approved" ? "Application approved — organisation is now live." : "Application rejected.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Action failed. Super admin access required.");
    }
  }

  return (
    <>
      <PageHead
        title="Onboarding queue"
        subtitle="Applications from organisations wanting to join the Daan Amrut network."
        action={
          <button
            onClick={() =>
              downloadCsv("partner-applications", [
                ["ID", "Organisation", "Type", "Domain", "City", "Contact", "Email", "Phone", "Status", "Submitted"],
                ...applications.map((a) => [
                  a.id,
                  a.org_name,
                  a.org_type ?? "",
                  a.domain ?? "",
                  a.city ?? "",
                  a.contact_name,
                  a.contact_email,
                  a.contact_phone ?? "",
                  a.status,
                  a.created_at,
                ]),
              ])
            }
            className="rounded-full border border-border bg-background px-4 py-2 text-xs font-semibold"
          >
            Export CSV
          </button>
        }
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total applications" value={String(applications.length)} tone="primary" />
        <StatCard label="Pending review" value={String(count("pending"))} tone="warn" />
        <StatCard label="Approved" value={String(count("approved"))} tone="good" />
        <StatCard label="Rejected" value={String(count("rejected"))} />
      </div>

      {/* AI Onboarding Risk Scanner */}
      <div className="mt-6">
        <AIFraudDetector
          application={{
            org_name: applications[0]?.org_name ?? "Shree Ahmedabad Panjrapole Trust",
            reg_80g: (applications[0] as any)?.reg_80g ?? "80G/AACTA1234F/2021",
            reg_12a: (applications[0] as any)?.reg_12a ?? "12A/AACTA1234F/2019",
            registration_no: (applications[0] as any)?.registration_no ?? "GUJ/AHM/1042/2003",
            animals_cared: (applications[0] as any)?.animals_cared ?? 1800,
            about: (applications[0] as any)?.about ?? "Verified animal shelter operating in Gujarat with 100+ staff caring for injured cattle and stray animals.",
            city: applications[0]?.city ?? "Ahmedabad",
          }}
        />
      </div>

      <div className="mt-6">
        <Panel title="Applications">
          {isLoading && <p className="py-6 text-sm text-foreground/70">Loading applications…</p>}
          {error && (
            <p className="py-6 text-sm text-[var(--destructive)]">
              You need to be signed in as a foundation super admin to view the onboarding queue.
            </p>
          )}
          {!isLoading && !error && applications.length === 0 && (
            <p className="py-6 text-sm text-foreground/70">No applications yet. New submissions from /partner-apply land here.</p>
          )}
          {applications.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="text-xs uppercase tracking-wider text-foreground/70">
                <tr className="border-b border-border text-left">
                  <th className="py-2">Organisation</th><th>Domain</th><th>City</th><th>Contact</th><th>Status</th><th>Reviewer note</th><th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {applications.map((a) => (
                  <tr key={a.id}>
                    <td className="py-3 font-medium">{a.org_name}<span className="block text-xs text-foreground/70">{a.org_type}</span></td>
                    <td>{a.domain ? domainName(a.domain) : "—"}</td>
                    <td>{a.city ?? "—"}</td>
                    <td className="text-xs">{a.contact_name}<span className="block text-foreground/70">{a.contact_email}</span></td>
                    <td className="capitalize">{a.status}</td>
                    <td>
                      <input
                        value={note[a.id] ?? a.reviewer_note ?? ""}
                        onChange={(e) => setNote((n) => ({ ...n, [a.id]: e.target.value }))}
                        placeholder="Optional note"
                        className="w-40 rounded-lg border border-border bg-background px-2 py-1 text-xs"
                      />
                    </td>
                    <td className="whitespace-nowrap text-right">
                      <button
                        disabled={review.isPending || a.status === "approved"}
                        onClick={() => act(a.id, "approved")}
                        className="rounded-full bg-[var(--success)] px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        disabled={review.isPending || a.status === "rejected"}
                        onClick={() => act(a.id, "rejected")}
                        className="ml-2 rounded-full bg-[var(--destructive)] px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </Panel>
      </div>
    </>
  );
}
